/** Service Desk console: the commerce profile setup and the Ordering & approvals settings (SD-S2-21, WP-S2-H). */
export const serviceCommerce = {
  nav: { commerce: "Commerce profile", ordering: "Ordering & approvals" },
  commerce: {
    intro: "A commerce profile ties your order system to the catalogue and orders: which connector it is reached through, which of its operations does each job, and what its statuses mean here. The connector's own sign-in, operations, logs and health stay in Connectors.",
    kind: { generic_rest: "REST connector", manual_csv: "CSV catalogue" },
    requirement: { required: "Required", recommended: "Recommended", optional: "Optional" },
    roles: {
      list_products: "List products",
      get_price: "Get price",
      get_stock: "Get stock",
      lookup_customer: "Look up customer",
      create_order: "Create order",
      get_order: "Get order",
      list_orders: "List orders",
      cancel_order: "Cancel order",
      create_quote: "Create quote",
      get_quote: "Get quote",
      get_invoice_status: "Get invoice status",
    },
    noAccess: { title: "You can't see the commerce profile", body: "Ask an owner for access to connectors." },
    error: { title: "We couldn't load the commerce profile", body: "Your order system is not affected. Try again." },
    empty: { title: "No commerce profile yet", readOnly: "Ask someone who manages connectors to set the profile up." },
    create: {
      title: "Set up the commerce profile",
      intro: "Pick how your catalogue and orders reach your order system. You can bind the operations and map statuses next.",
      kind: "How is your catalogue kept?",
      rest: "Through a connector",
      restHint: "Products, prices and orders go through an API you have connected.",
      csv: "From a CSV file",
      csvHint: "No API: you upload the catalogue and key orders into your system yourself.",
      connector: "Connector",
      connectorHint: "Create the connector first under Connectors if it isn't listed.",
      chooseConnector: "Choose a connector",
      noConnectors: "No connectors yet",
      name: "Profile name",
      nameHint: "Shown to staff only.",
      submit: "Create profile",
      done: "The commerce profile was created.",
      problem: { name: "Give the profile a name.", connector: "Choose the connector." },
    },
    readiness: {
      catalogue: "Catalogue",
      ordering: "Ordering",
      paused: "Ordering paused",
      missing: "Bind the required operations to turn ordering on: {{roles}}.",
    },
    reason: {
      paused: "Ordering is paused.",
      no_connector: "No connector is attached.",
      connector_unhealthy: "The connector is unhealthy.",
      missing_required: "A required operation is not bound.",
    },
    actions: {
      pause: "Pause ordering",
      pausedDone: "Ordering is paused. Clients can still browse.",
      resume: "Resume ordering",
      resumed: "Ordering is back on.",
      validate: "Check bindings",
      valid: "Every binding meets the canonical contract.",
      invalid: "Some bindings need attention.",
      connector: "Connector settings",
      catalogue: "Catalogue",
    },
    bindings: {
      title: "Operations per commerce role",
      label: "Commerce roles bound to connector operations",
      role: "Commerce role",
      operation: "Connector operation",
      state: "State",
      notBound: "Not bound",
      bindFor: "Operation for {{role}}",
      boundDone: "{{role}} is bound.",
      unboundDone: "{{role}} is unbound.",
      openOperation: "Open the operation",
      noConnector: "No connector is attached to this profile, so there are no operations to bind.",
      missing: "Required: not bound",
      unbound: "Not bound",
      ok: "Meets the contract",
      problems_one: "{{count}} problem",
      problems_other: "{{count}} problems",
    },
    problem: {
      operation_unknown: "The operation doesn't exist",
      not_published: "The operation isn't published",
      version_unknown: "That version doesn't exist",
      kind_mismatch: "Wrong kind of operation for this role",
      format_not_json: "The response isn't JSON",
      mapping_missing: "Mapping missing",
      mapping_invalid: "Mapping invalid",
      list_needs_items: "A list needs an items path",
      output_missing: "A required output isn't mapped",
      output_one_of: "Map at least one of the alternatives",
      output_needed_for_batch: "Needed for batch calls",
      input_missing: "A required input isn't used",
      paging_missing: "Paging isn't set up",
      idempotency_missing: "No idempotency key is sent",
      reject_reasons_missing: "Refusal reasons aren't mapped",
      secret_in_request: "A secret is written into the request",
      credential_header: "A credential header is typed by hand",
    },
    statusMap: {
      title: "Status mapping",
      label: "Order statuses of your system and what they mean here",
      intro: "Map the statuses your system reports to the statuses clients see. A status that isn't in the table is read as “{{fallback}}” and flagged.",
      external: "Status in your system",
      canonical: "Means here",
      choose: "Choose a status",
      noReason: "No reason",
      empty: "No statuses mapped yet. Every status your system reports will be read as processing.",
      externalFor: "Status {{n}} in your system",
      canonicalFor: "What status {{n}} means here",
      reasonFor: "Refusal reason for status {{n}}",
      removeRow: "Remove status {{n}}",
      remove: "Remove",
      add: "Add status",
      save: "Save mapping",
      saved: "The status mapping was saved.",
      problem: { incomplete: "Fill in both columns for every row.", duplicate: "These statuses are mapped twice: {{status}}." },
      tryLabel: "Try a status",
      tryHint: "Type a status your system sends to see how it is read.",
      try: "Check",
      resolved: "Read as {{status}}",
      unmapped: "Not in the table: read as processing",
    },
  },
  ordering: {
    save: "Save changes",
    saved: "Ordering settings saved.",
    profileLink: "Commerce profile",
    empty: { title: "Set up the commerce profile first", body: "Approval rules and ordering limits belong to a commerce profile.", cta: "Open the commerce profile" },
    problem: { pesos: "Enter a whole number of pesos." },
    approval: {
      title: "Approval rules",
      intro: "Clients approve their own orders in their portal. These rules add a decision by your team.",
      threshold: "Staff approval above (₱)",
      thresholdHint: "An order above this amount waits for a team member before it is sent. Leave empty for no threshold.",
      none: "No threshold",
      credit: "Approve orders of organisations on credit hold",
      creditHint: "When your system reports a credit hold, a team member decides.",
      anomaly: "Check for unusually large orders",
      anomalyHint: "An order well above the organisation's largest in the last 90 days needs a team member.",
      approvers: "Approvers are team members whose role has “Approve orders”. Give the permission under Team roles.",
    },
    categories: {
      label: "Categories that always need approval",
      hint: "Any order with a product of these categories waits for a team member.",
      add: "Add",
      list: "Categories needing approval",
      remove: "Remove {{category}}",
    },
    stepUp: {
      title: "Step-up verification",
      intro: "Large orders need a fresh e-mail code from the client before they are confirmed.",
      threshold: "Ask for a code at or above (₱)",
      hint: "A whole number of pesos.",
    },
    stock: {
      title: "Stock and backorders",
      intro: "What happens when a client orders more than is in stock.",
      backorder: "Allow backorders",
      backorderHint: "Clients can order items that are out of stock; the lead time is shown.",
      staffConfirm: "Orders staff place wait for the client",
      staffConfirmHint: "An order you enter for a client is confirmed by the client, not on their behalf.",
    },
    cancel: {
      title: "Cancellation policy",
      intro: "What happens when a client asks to cancel an order that is already placed.",
      atSystem: "Cancel at the order system right away",
      atSystemHint: "Runs the cancel operation at once. Off: the team is asked and cancels it there.",
    },
  },
} as const;
